import type {
  DocumentVariableDefinition,
} from '@/lib/documentTemplateVariables';

import {
  DOCUMENT_VARIABLE_GROUP_LABELS,
} from '@/lib/documentTemplateVariables';

type Props = {
  definitions:
    DocumentVariableDefinition[];

  automaticValues:
    Record<string, string>;

  resolvedValues:
    Record<string, string>;

  manualValues:
    Record<string, string>;

  preservingSnapshot:
    boolean;

  onChange:
    (
      key: string,
      value: string
    ) => void;

  onReset:
    (
      key: string
    ) => void;

  onRefreshAutomatic:
    () => void;
};

export default function DocumentVariablesPanel({
  definitions,
  automaticValues,
  resolvedValues,
  manualValues,
  preservingSnapshot,
  onChange,
  onReset,
  onRefreshAutomatic,
}: Props) {
  const required =
    definitions.filter(
      (item) =>
        item.required
    );

  const missingRequired =
    required.filter(
      (item) =>
        !String(
          resolvedValues[
            item.key
          ] ?? ''
        ).trim()
    );

  if (
    definitions.length === 0
  ) {
    return (
      <div className="rounded-xl border border-background-200 bg-background-50 px-4 py-4">
        <div className="flex items-center gap-2 text-sm font-semibold text-foreground-700">
          <i className="ri-braces-line text-primary-500"></i>
          Variáveis do documento
        </div>

        <p className="text-xs text-foreground-400 mt-2">
          O modelo selecionado não possui variáveis configuradas.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-background-200 bg-background-50 p-4">
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-4">
        <div>
          <div className="flex items-center gap-2">
            <i className="ri-braces-line text-primary-500"></i>

            <h4 className="text-sm font-semibold text-foreground-800">
              Preenchimento inteligente
            </h4>
          </div>

          <p className="text-[11px] text-foreground-400 mt-1">
            Valores automáticos podem ser substituídos apenas neste documento.
          </p>
        </div>

        {preservingSnapshot && (
          <button
            type="button"
            onClick={
              onRefreshAutomatic
            }
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-blue-200 bg-blue-50 text-[11px] font-medium text-blue-700 hover:bg-blue-100 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-refresh-line"></i>
            Atualizar com dados atuais
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
        <div className="rounded-lg bg-background-100 px-3 py-2">
          <span className="block text-[10px] uppercase tracking-wide text-foreground-400">
            Total
          </span>

          <strong className="text-sm text-foreground-800">
            {definitions.length}
          </strong>
        </div>

        <div className="rounded-lg bg-background-100 px-3 py-2">
          <span className="block text-[10px] uppercase tracking-wide text-foreground-400">
            Obrigatórias
          </span>

          <strong className="text-sm text-foreground-800">
            {required.length}
          </strong>
        </div>

        <div className="rounded-lg bg-background-100 px-3 py-2">
          <span className="block text-[10px] uppercase tracking-wide text-foreground-400">
            Faltantes
          </span>

          <strong className={`text-sm ${
            missingRequired.length > 0
              ? 'text-rose-600'
              : 'text-emerald-600'
          }`}>
            {missingRequired.length}
          </strong>
        </div>

        <div className="rounded-lg bg-background-100 px-3 py-2">
          <span className="block text-[10px] uppercase tracking-wide text-foreground-400">
            Manuais
          </span>

          <strong className="text-sm text-foreground-800">
            {
              Object.keys(
                manualValues
              ).length
            }
          </strong>
        </div>
      </div>

      {preservingSnapshot && (
        <div className="mb-4 rounded-lg border border-violet-200 bg-violet-50 px-3 py-2 text-xs text-violet-800">
          <i className="ri-shield-check-line mr-1.5"></i>
          O documento está usando o <strong>snapshot salvo na versão original</strong>.
          Os cadastros atuais não substituirão esses valores até que você mande atualizá-los.
        </div>
      )}

      {missingRequired.length > 0 && (
        <div className="mb-4 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800">
          <div className="font-semibold">
            Existem variáveis obrigatórias sem valor
          </div>

          <p className="mt-1">
            {
              missingRequired
                .map(
                  (item) =>
                    item.label
                )
                .join(', ')
            }.
          </p>
        </div>
      )}

      <div className="space-y-2">
        {definitions.map(
          (definition) => {
            const automaticValue =
              String(
                automaticValues[
                  definition.key
                ] ?? ''
              );

            const resolvedValue =
              String(
                resolvedValues[
                  definition.key
                ] ?? ''
              );

            const manual =
              Object.prototype
                .hasOwnProperty
                .call(
                  manualValues,
                  definition.key
                );

            const missing =
              definition.required &&
              !resolvedValue.trim();

            const sourceLabel =
              manual
                ? 'Manual'
                : preservingSnapshot
                  ? 'Snapshot salvo'
                  : automaticValue.trim()
                    ? 'Automático'
                    : 'Sem valor';

            return (
              <div
                key={
                  definition.key
                }
                className={`rounded-lg border p-3 ${
                  missing
                    ? 'border-rose-200 bg-rose-50/40'
                    : 'border-background-200'
                }`}
              >
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2 mb-2">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="text-xs font-semibold text-foreground-800">
                        {definition.label}
                      </span>

                      <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${
                        definition.required
                          ? 'bg-rose-100 text-rose-700'
                          : 'bg-background-100 text-foreground-500'
                      }`}>
                        {definition.required
                          ? 'Obrigatória'
                          : 'Opcional'}
                      </span>

                      <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${
                        manual
                          ? 'bg-amber-100 text-amber-700'
                          : preservingSnapshot
                            ? 'bg-violet-100 text-violet-700'
                            : automaticValue.trim()
                              ? 'bg-emerald-100 text-emerald-700'
                              : 'bg-background-100 text-foreground-500'
                      }`}>
                        {sourceLabel}
                      </span>
                    </div>

                    <p className="text-[10px] text-foreground-400 mt-1">
                      <span className="font-mono">
                        {'{{'}
                        {definition.key}
                        {'}}'}
                      </span>

                      {' · '}

                      {
                        DOCUMENT_VARIABLE_GROUP_LABELS[
                          definition.group
                        ]
                      }
                    </p>
                  </div>

                  {manual && (
                    <button
                      type="button"
                      onClick={() =>
                        onReset(
                          definition.key
                        )
                      }
                      className="text-[10px] text-primary-600 hover:text-primary-700 cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-arrow-go-back-line mr-1"></i>
                      Remover substituição
                    </button>
                  )}
                </div>

                <input
                  type="text"
                  value={
                    resolvedValue
                  }
                  onChange={
                    (event) =>
                      onChange(
                        definition.key,
                        event.target.value
                      )
                  }
                  placeholder={
                    definition.required
                      ? 'Valor obrigatório'
                      : 'Sem valor'
                  }
                  className={`w-full px-3 py-2 bg-background-50 border rounded-lg text-sm text-foreground-800 focus:outline-none ${
                    missing
                      ? 'border-rose-300 focus:border-rose-400'
                      : 'border-background-200 focus:border-primary-300'
                  }`}
                />

                {!preservingSnapshot && !manual && (
                  <p className="text-[10px] text-foreground-400 mt-1">
                    Valor obtido automaticamente dos dados atuais do sistema.
                  </p>
                )}

                {manual && (
                  <p className="text-[10px] text-amber-700 mt-1">
                    Valor substituído somente para este documento. O cadastro original não será alterado.
                  </p>
                )}

                {preservingSnapshot && !manual && (
                  <p className="text-[10px] text-violet-700 mt-1">
                    Valor preservado do snapshot histórico deste documento.
                    {automaticValue !== resolvedValue && (
                      <>
                        {' '}
                        Valor atual do sistema: <strong>{automaticValue || '—'}</strong>.
                      </>
                    )}
                  </p>
                )}
              </div>
            );
          }
        )}
      </div>
    </div>
  );
}